import {
  type FC,
  Fragment,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Box,
  Button,
  type ButtonProps,
  CheckIcon,
  Column,
  ConfirmationDialog,
  Drawer,
  Paragraph,
  Row,
  SectionHeading,
  Spinner,
  Tooltip,
  Text,
  type BoxProps,
} from "@hightouch/htui";

import { noop } from "lodash";
import { Watermark } from "src/partner/watermark";
import { Container } from "src/ui/box";
import { useNavigation } from "src/router";
import { motion } from "motion/react";
import { useDragScroll } from "src/components/explore/draggable";
import { lockDocumentScroll } from "src/utils/document-scroll-lock";

export interface ResourceWizardProps {
  /**
   * Whether the user has made any changes that would be lost.
   * Determines if the user can exit the wizard without confirmation.
   */
  hasChanges?: boolean;
  fullscreen?: boolean;
  title: ReactNode;
  watermark?: boolean;
  step: number;
  steps: ResourceWizardStep[];
  previousDisabled?: boolean;
  sx?: BoxProps["sx"];
  showOverlay?: boolean;
  setStep: (step: number) => void;
  onCancel: () => void;
  onSubmit: () => void | Promise<void>;
}

export type ResourceWizardStep = {
  title?: string;
  header?: ReactNode;
  render: FC;
  /**
   * Docked beside the step's container as a flex sibling — the step content
   * shrinks to make room. Used by the audience agent rail, which sticks to
   * the region between the wizard header and the bottom action bar.
   */
  sidebar?: ReactNode;
  bg?: string;
  loading?: boolean;
  submitting?: boolean;
  continueLabel?: string;
  continueProps?: Pick<ButtonProps, "form" & "type">;
  continueTooltip?: string;
  continue?: ReactNode;
  actions?: ReactNode;
  disabled?: boolean; // Disable the continue/finish button
  previousDisabled?: boolean;
  previousTooltip?: string;
  onBack?: () => void;
  onContinue?: () => void | Promise<void>;
};

const size = "medium";
export const BOTTOM_BAR_HEIGHT_PX = 80;
export const WIZARD_HEADER_SIZE_PX = 64;

export const ResourceWizard: FC<Readonly<ResourceWizardProps>> = ({
  hasChanges = true,
  title,
  showOverlay = true,
  step,
  steps,
  previousDisabled,
  fullscreen,
  watermark,
  setStep,
  onCancel,
  onSubmit,
}) => {
  const navigation = useNavigation();

  const data = steps[step];
  const [submitting, setSubmitting] = useState(false);
  const [exiting, setExiting] = useState(false);

  // Add ref to prevent closure issues with onCancel
  // in cases where onCancel is not stable
  const cancelRef = useRef(noop);
  cancelRef.current = onCancel;

  const handleExit = useCallback(() => {
    if (hasChanges) {
      setExiting(true);
    } else {
      cancelRef.current();
    }
  }, [hasChanges]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [step]);

  const dragContainerRef = useDragScroll();

  // htui drawers don't take focus, and this one covers the screen, so whatever held focus would sit hidden behind it.
  // A frame later, because the drawer's portal renders its content after this mounts.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const content = dragContainerRef.current?.closest<HTMLElement>(
        ".chakra-modal__content",
      );
      if (content && !content.contains(document.activeElement)) {
        content.focus({ preventScroll: true });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [dragContainerRef]);

  // Chakra's drawer only blocks scroll input; root overflow stays visible, so
  // a body-anchored overlay poking past the viewport (a tooltip, a popover)
  // would still summon the root scrollbar and narrow this fixed, viewport-sized
  // drawer. Hide root overflow for the drawer's lifetime. That also makes the
  // app's reserved gutter (`scrollbar-gutter: stable` in global-styles) a blank
  // strip along the drawer's right edge, so release it and run edge to edge.
  useLayoutEffect(() => {
    const html = document.documentElement;
    const previousGutter = html.style.scrollbarGutter;
    const releaseScroll = lockDocumentScroll();
    html.style.scrollbarGutter = "auto";
    return () => {
      releaseScroll();
      html.style.scrollbarGutter = previousGutter;
    };
  }, []);

  const isLastStep = step === steps.length - 1;

  const stepContainer = (
    <Container
      size={fullscreen ? "unset" : size}
      px={fullscreen ? 0 : 24}
      py={fullscreen ? 0 : 8}
    >
      <Column flex={1}>
        {data?.loading ? (
          <Spinner m="auto" size="lg" />
        ) : (
          <Suspense fallback={<Spinner m="auto" size="lg" />}>
            {/* Remount the step when the title or step changes to make sure react mounts the controllers instead of reconciliation */}
            <Fragment key={data?.title ?? step}>{data?.render({})}</Fragment>
          </Suspense>
        )}
      </Column>
    </Container>
  );

  return (
    <>
      <Drawer
        isOpen
        onClose={handleExit}
        showOverlay={showOverlay}
        size="full"
        contentWrapperStyles={{ margin: 0 }}
      >
        <Column
          as={motion.div}
          layoutScroll
          ref={dragContainerRef}
          minHeight="100vh"
          overflowY="auto"
          bg={data?.bg}
        >
          <Row
            flexShrink={0}
            height="64px"
            bg="white"
            zIndex={2}
            justifyContent="center"
            width="100%"
            borderBottomWidth="hairline"
            borderBottomStyle="solid"
            borderColor="base.border"
            position="sticky"
            top={0}
          >
            <Row alignItems="center" flex={1} px={8}>
              <SectionHeading>{title}</SectionHeading>
              <Row flex={1} alignItems="center" justifyContent="center" mx={8}>
                {steps.map(({ title }, i) => {
                  const visited = i < step;
                  const current = i === step;
                  return (
                    <Fragment key={i}>
                      <Row gap={2} alignItems="center">
                        <Row
                          flexShrink={0}
                          alignItems="center"
                          justifyContent="center"
                          borderColor="purple.900"
                          border={
                            visited
                              ? "none"
                              : "var(--chakra-border-hairline) solid"
                          }
                          bg={
                            visited
                              ? "gray.base"
                              : current
                                ? "teal.700"
                                : "white"
                          }
                          width="14px"
                          height="14px"
                          fontSize="14px"
                          borderRadius="7px"
                        >
                          {i < step && <CheckIcon color="white" />}
                        </Row>
                        {title && (
                          <Box display={["none", "none", "block", "block"]}>
                            <Text
                              color={visited ? "text.secondary" : "teal.700"}
                              fontWeight="semibold"
                            >
                              {title}
                            </Text>
                          </Box>
                        )}
                      </Row>

                      {i < steps?.length - 1 && (
                        <Row
                          height="0.5px"
                          bg="gray.border"
                          flex={1}
                          mx={4}
                          maxWidth="48px"
                        />
                      )}
                    </Fragment>
                  );
                })}
              </Row>

              <Button variant="secondary" onClick={handleExit}>
                Exit
              </Button>
            </Row>
          </Row>
          {data?.header && (
            <Column position="sticky" top="64px" bg="white" zIndex={2}>
              <Row
                width="100%"
                height="80px"
                bg="gray.50"
                justifyContent="center"
                position="sticky"
              >
                <Container size={size} px={24}>
                  <Row alignItems="center" flex={1}>
                    {data?.header}
                  </Row>
                </Container>
              </Row>
            </Column>
          )}

          {data?.sidebar ? (
            // The sidebar docks beside the step content: the content column
            // shrinks and the sidebar spans the region between the sticky
            // header and bottom bar. Steps without one keep the container as
            // a direct child, so other wizards' layout (including horizontal
            // drag-scroll overflow) is untouched.
            <Row flex={1} alignItems="stretch" width="100%">
              {/* The content plane sits above the sidebar so the docked rail
                  reads as the layer beneath it — like the page card over the
                  page gutter. Adding `zIndex 1` ensures that this sibling
                  sits above the sidebar so the shadow paints onto the
                  sidebar. */}
              <Column flex={1} minWidth={0} boxShadow="sm" zIndex={1}>
                {stepContainer}
              </Column>
              {data.sidebar}
            </Row>
          ) : (
            stepContainer
          )}
          <Row
            flexShrink={0}
            position="sticky"
            bottom={0}
            bg="white"
            height={`${BOTTOM_BAR_HEIGHT_PX}px`}
            alignItems="flex-end"
            justifyContent="center"
            zIndex={2}
          >
            <Row
              justifyContent="center"
              alignItems="center"
              height={`${BOTTOM_BAR_HEIGHT_PX}px`}
              bg="white"
              width="100%"
              borderTopWidth="hairline"
              borderTopStyle="solid"
              borderColor="base.border"
            >
              <Container size={size} px={24}>
                <Row
                  alignItems="center"
                  flex={1}
                  justifyContent="space-between"
                  width="100%"
                >
                  <Box
                    display="contents"
                    visibility={step > 0 ? "visible" : "hidden"}
                  >
                    <Tooltip
                      isDisabled={!data?.previousTooltip}
                      message={data?.previousTooltip || ""}
                    >
                      <Button
                        icon={ArrowLeftIcon}
                        isDisabled={
                          data?.loading ||
                          data?.previousDisabled ||
                          previousDisabled
                        }
                        size="lg"
                        variant="secondary"
                        onClick={data?.onBack ?? (() => setStep(step - 1))}
                      >
                        Go back
                      </Button>
                    </Tooltip>
                  </Box>
                  {watermark && <Watermark />}
                  {!data?.loading && (
                    <Row alignItems="center" gap={4}>
                      {data?.actions || null}
                      {data?.continue ? (
                        <Text
                          fontWeight="semibold"
                          size="sm"
                          color="text.tertiary"
                        >
                          {data?.continue}
                        </Text>
                      ) : (
                        <Tooltip
                          isDisabled={!data?.continueTooltip}
                          message={data?.continueTooltip || ""}
                        >
                          <Button
                            {...(data?.continueProps || {})}
                            directionIcon={
                              isLastStep ? undefined : ArrowRightIcon
                            }
                            isDisabled={data?.disabled}
                            isLoading={
                              data?.submitting ||
                              submitting ||
                              navigation.state === "loading"
                            }
                            size="lg"
                            variant="primary"
                            onClick={async () => {
                              setSubmitting(true);
                              try {
                                // Call custom onContinue handler if it exists
                                if (data?.onContinue) {
                                  await data?.onContinue();
                                }

                                // Always submit on last step
                                if (isLastStep) {
                                  await onSubmit();
                                } else if (!data?.onContinue) {
                                  // Only advance to next step if there was no custom handler
                                  setStep(step + 1);
                                }
                              } catch {
                                // Errors already handled by form handlers
                              } finally {
                                setSubmitting(false);
                              }
                            }}
                          >
                            {data?.continueLabel ??
                              (isLastStep ? "Finish" : "Continue")}
                          </Button>
                        </Tooltip>
                      )}
                    </Row>
                  )}
                </Row>
              </Container>
            </Row>
          </Row>
        </Column>
      </Drawer>
      <ConfirmationDialog
        confirmButtonText="Exit"
        isOpen={exiting}
        title="Are you sure?"
        variant="warning"
        onClose={() => setExiting(false)}
        onConfirm={() => {
          setExiting(false);
          onCancel();
        }}
      >
        <Paragraph>Your progress will be lost.</Paragraph>
      </ConfirmationDialog>
    </>
  );
};
